// @ts-check
/**
 * Realtime client for the notify relay (Socket.IO).
 *
 * Shared by the admin dashboard and the customer app: keep the two copies
 * identical (admin src/services/websocket/realtimeClient.js,
 * enduser src/lib/realtime/realtimeClient.js). No imports on purpose, so it can
 * be tested with plain node and a fake socket.
 *
 * Contract (notify, docs/EVENTS.md):
 *   join::room   "room" | {room, since}   ack {ok, room, mux, last_seq} | {ok, room, mux, complete, last_seq, events}
 *   leave::room  "room"                   ack {ok, room}
 *   sync::room   {room, since}            ack {ok, room, complete, last_seq, events:[{seq, event, data}]}
 *   join::denied   {room, reason, code, retry}
 *   join::accepted {room}
 *   Relayed object payloads carry `_seq` (increasing per room) and, from notify
 *   versions whose join ack says `mux: true`, `_room` (the room they come from).
 *
 * Design
 * - ONE connection per tab and credential ("auth group"), carrying every room
 *   of that group. Events are routed to their room by `_room`; `_seq`, replay,
 *   dedupe and room-scoped listeners stay per room.
 * - Older notify (no `mux` in the join ack, or no ack at all): one connection
 *   per room, as before. The first join on a connection is the probe: the
 *   other rooms of the group wait for its ack, then either join the same
 *   connection or get their own.
 * - Rooms are ref-counted: join(room) returns a release function; the room is
 *   left (leave::room) shortly after the last release, and the connection is
 *   closed with its last room.
 * - Rooms that need a credential (`requiresAuth`) are not joined before one is
 *   available (no token-less join at page load); they are retried when
 *   authChanged() is called and on a slow backoff.
 * - Listeners are kept here, not on the sockets: joining/leaving a room never
 *   wipes them. on(event, cb) hears every room; on(event, cb, {room}) one room.
 * - Never gives up reconnecting (1 s -> 30 s backoff with jitter), and retries
 *   at once when the tab becomes visible again or the network comes back.
 * - On every re-join: {room, since: lastSeq} -> missed events are replayed
 *   (deduped by `_seq`). When the replay cannot vouch for completeness (server
 *   says complete:false, or no seq was known) the room's resync hooks run so
 *   each store refetches over REST.
 */

const INTERNAL_EVENTS = new Set(['join::denied', 'join::accepted']);
const SEEN_MAX = 1000;

/**
 * Exponential backoff with jitter: attempt 0 -> ~1 s, then doubling, capped.
 * @param {number} attempt
 * @param {{min?: number, max?: number, jitter?: number, random?: () => number}} [opts]
 */
export function backoffDelay(attempt, { min = 1000, max = 30000, jitter = 0.5, random = Math.random } = {}) {
  const base = Math.min(max, min * 2 ** Math.min(Math.max(attempt, 0), 16));
  const spread = base * jitter;
  return Math.max(0, Math.round(base - spread + random() * 2 * spread));
}

function defaultEnv() {
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  return {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id),
    now: () => Date.now(),
    // @ts-ignore
    window: typeof g.window !== 'undefined' ? g.window : (typeof g.addEventListener === 'function' ? g : null),
    // @ts-ignore
    document: typeof g.document !== 'undefined' ? g.document : null,
  };
}

const hasCredential = (auth) => !!(auth && (auth.token || auth.cert));

/** One room: ref count, sequence tracking, replay, resync hooks. */
class Channel {
  /**
   * @param {RealtimeClient} client
   * @param {string} room
   */
  constructor(client, room) {
    this.client = client;
    this.room = room;
    this.group = client.authGroup(room);
    this.refs = 0;
    /** @type {Connection | null} */
    this.conn = null;
    /** Highest `_seq` received for this room (null: none yet). */
    this.lastSeq = null;
    this.seen = new Set();
    this.seenOrder = [];
    this.joined = false;
    this.joining = false;
    this.everJoined = false;
    this.closed = false;
    this.attempt = 0;
    this.joinToken = 0;
    this.rejoinTimer = null;
    this.closeTimer = null;
    /** @type {Set<Function>} */
    this.resyncHooks = new Set();
  }

  get env() {
    return this.client.env;
  }

  get socket() {
    return this.conn ? this.conn.socket : null;
  }

  join() {
    const socket = this.socket;
    if (!socket || this.closed) return;
    const since = this.lastSeq;
    const context = this.everJoined ? 'reconnect' : null;
    // String form when nothing is known: the only form older notify versions accept.
    const arg = since != null ? { room: this.room, since } : this.room;
    const token = ++this.joinToken;
    this.joining = true;
    let done = false;
    const timer = this.env.setTimeout(() => {
      if (done || token !== this.joinToken) return;
      done = true;
      this.joining = false;
      this.onJoinTimeout(arg, context);
    }, this.client.ackTimeout);
    socket.emit('join::room', arg, (res) => {
      if (done || token !== this.joinToken) return;
      done = true;
      this.joining = false;
      this.env.clearTimeout(timer);
      this.onJoinAck(res, since, context);
    });
  }

  /** No ack: a notify without acks. Assume joined; replay unknown; no multiplexing. */
  onJoinTimeout(arg, context) {
    const conn = this.conn;
    if (!conn?.socket?.connected) return;
    if (typeof arg !== 'string') conn.socket.emit('join::room', this.room);
    this.joined = true;
    this.everJoined = true;
    if (context) this.resync(context);
    conn.learn(this, null);
  }

  onJoinAck(res, since, context) {
    const conn = this.conn;
    if (res && res.ok === true) {
      this.joined = true;
      this.everJoined = true;
      this.attempt = 0;
      this.applySync(res, since, context);
    } else if (res && (res.reason === 'rate_limited' || res.reason === 'too_large')) {
      // Authorization refusals also arrive as join::denied (handled there).
      // Rate/size limits only come back here: try again later.
      this.scheduleRejoin();
    }
    if (conn && this.conn === conn) conn.learn(this, res);
  }

  /**
   * Apply a join/sync result: replay events, move lastSeq, and decide whether the
   * stores must refetch. `context` null = first join (the stores just fetched).
   */
  applySync(res, since, context) {
    const serverLast = typeof res?.last_seq === 'number' ? res.last_seq : null;
    if (since != null && serverLast != null && serverLast < since) {
      // Numbers from another generation (replay store reset): start over.
      this.resetSeen();
    }
    const events = Array.isArray(res?.events) ? res.events.slice() : [];
    events.sort((a, b) => (a?.seq ?? 0) - (b?.seq ?? 0));
    for (const e of events) {
      if (e && typeof e.event === 'string' && !INTERNAL_EVENTS.has(e.event)) {
        this.receive(e.event, e.data, true, typeof e.seq === 'number' ? e.seq : null);
      }
    }
    if (serverLast != null && (this.lastSeq == null || serverLast > this.lastSeq)) this.lastSeq = serverLast;
    if (context && !(since != null && res?.complete === true)) this.resync(context);
  }

  receive(event, data, replayed, seqHint) {
    let seq = data && typeof data === 'object' && typeof data._seq === 'number' ? data._seq : null;
    if (seq == null && typeof seqHint === 'number') seq = seqHint;
    if (seq != null) {
      if (this.seen.has(seq)) return;
      this.remember(seq);
      if (this.lastSeq == null || seq > this.lastSeq) this.lastSeq = seq;
    }
    this.client.dispatch(this.room, event, data, { room: this.room, seq, replayed });
  }

  remember(seq) {
    this.seen.add(seq);
    this.seenOrder.push(seq);
    if (this.seenOrder.length > SEEN_MAX) this.seen.delete(this.seenOrder.shift());
  }

  resetSeen() {
    this.seen.clear();
    this.seenOrder = [];
    this.lastSeq = null;
  }

  resync(reason) {
    this.client.logger?.info?.(`[realtime] ${this.room}: refetch (${reason})`);
    for (const hook of Array.from(this.resyncHooks)) {
      try {
        hook({ room: this.room, reason });
      } catch (error) {
        this.client.logger?.error?.('[realtime] resync hook failed:', error);
      }
    }
  }

  /** The connection dropped: the next connect re-joins. */
  detached() {
    this.joined = false;
    this.joining = false;
    this.joinToken++;
    this.clearRejoinTimer();
  }

  onAccepted() {
    this.joined = true;
    this.everJoined = true;
    this.sync('accepted');
  }

  /** Ask for what was missed since lastSeq; resync hooks run if it cannot be proven complete. */
  sync(context, { onTimeout } = /** @type {{onTimeout?: Function}} */ ({})) {
    const socket = this.socket;
    if (!socket?.connected) return;
    const since = this.lastSeq;
    const token = this.joinToken;
    let done = false;
    const timer = this.env.setTimeout(() => {
      if (done) return;
      done = true;
      if (onTimeout) onTimeout();
      else if (token === this.joinToken) this.resync(context);
    }, this.client.ackTimeout);
    socket.emit('sync::room', { room: this.room, since }, (res) => {
      if (done) return;
      done = true;
      this.env.clearTimeout(timer);
      if (token !== this.joinToken) return;
      if (!res || res.ok !== true) {
        this.resync(context);
        return;
      }
      this.applySync(res, since, context);
    });
  }

  scheduleRejoin() {
    if (this.rejoinTimer || this.closed) return;
    const delay = backoffDelay(this.attempt++, { random: this.client.random });
    this.rejoinTimer = this.env.setTimeout(() => {
      this.rejoinTimer = null;
      if (this.closed || !this.socket?.connected || this.joined) return;
      this.join();
    }, delay);
  }

  clearRejoinTimer() {
    if (this.rejoinTimer) {
      this.env.clearTimeout(this.rejoinTimer);
      this.rejoinTimer = null;
    }
  }
}

/** One Socket.IO connection, carrying one room (old notify) or every room of an auth group. */
class Connection {
  /**
   * @param {RealtimeClient} client
   * @param {string} key
   * @param {string} group
   * @param {boolean | null} mux
   */
  constructor(client, key, group, mux) {
    this.client = client;
    this.key = key;
    this.group = group;
    /** true: the server stamps `_room` (several rooms here); false: one room; null: unknown yet. */
    this.mux = mux;
    /** @type {Map<string, Channel>} */
    this.channels = new Map();
    /** @type {any} */
    this.socket = null;
    this.closed = false;
    this.attempt = 0;
    this.reconnectTimer = null;
    this.lastAuthRefresh = -Infinity;
    /** Whether the last handshake carried a credential. */
    this.hadCredential = false;
    this.wakeGen = 0;
    this.restartedGen = -1;
  }

  get env() {
    return this.client.env;
  }

  /** A room whose getAuth() gives this connection's credential. */
  authRoom() {
    const first = this.channels.keys().next();
    return first.done ? null : first.value;
  }

  open() {
    const c = this.client;
    const socket = c.io(c.url, {
      transports: ['websocket'],
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
      timeout: 20000,
      ...c.socketOptions,
      // A function: every (re)connection sends the current credential.
      auth: (cb) => {
        let auth = {};
        try {
          const room = this.authRoom();
          auth = (room && c.getAuth && c.getAuth(room)) || {};
        } catch {
          auth = {};
        }
        this.hadCredential = hasCredential(auth);
        cb(auth);
      },
    });
    this.socket = socket;
    socket.on('connect', () => this.onConnect());
    socket.on('disconnect', (reason) => this.onDisconnect(reason));
    socket.on('connect_error', (err) => this.onConnectError(err));
    socket.on('join::denied', (p) => this.onDenied(p));
    socket.on('join::accepted', (p) => this.onAccepted(p));
    socket.onAny((event, ...args) => {
      if (INTERNAL_EVENTS.has(event)) return;
      this.onEvent(event, args[0]);
    });
  }

  /** The channel an event or a notice belongs to, or null. */
  route(room) {
    if (typeof room === 'string') return this.channels.get(room) || null;
    if (this.channels.size === 1) return this.channels.values().next().value || null;
    return null;
  }

  onEvent(event, data) {
    const tagged = data && typeof data === 'object' && typeof data._room === 'string';
    if (tagged) {
      const ch = this.channels.get(data._room);
      // A room left a moment ago (leave in flight): drop, never leak it to another room.
      if (ch) ch.receive(event, data, false, null);
      return;
    }
    const ch = this.route(null);
    if (ch) {
      ch.receive(event, data, false, null);
      return;
    }
    // Several rooms but no `_room`: an older notify answered (rolling update) or a
    // non-object payload. Deliver to unscoped listeners only, and stop sharing.
    this.client.dispatch(null, event, data, { room: null, seq: null, replayed: false });
    if (data && typeof data === 'object') this.demote();
  }

  onConnect() {
    this.attempt = 0;
    this.clearReconnectTimer();
    if (Array.from(this.channels.values()).some((ch) => this.client.requiresAuth(ch.room)) && !this.hadCredential) {
      // Opened before the credential existed (or it was wiped): those rooms wait.
      this.client.pendAuth(this);
    }
    this.joinWaiting();
  }

  /** Join the channels that are not joined yet (all of them after a reconnect). */
  joinWaiting() {
    if (!this.socket?.connected) return;
    for (const ch of Array.from(this.channels.values())) {
      if (ch.joined || ch.joining) continue;
      if (this.client.requiresAuth(ch.room) && !this.hadCredential) continue;
      // Unknown server: probe with one room, the others wait for its ack.
      if (this.mux == null && Array.from(this.channels.values()).some((o) => o !== ch && o.joining)) continue;
      ch.join();
      if (this.mux == null) break;
    }
  }

  /** First ack on this connection tells whether the server multiplexes. */
  learn(ch, res) {
    if (this.mux != null) {
      if (this.mux && res && !res.mux && this.channels.size > 1) this.demote();
      return;
    }
    this.mux = !!(res && res.mux === true);
    this.client.mux = this.mux;
    if (this.mux) this.joinWaiting();
    else this.demote(ch);
  }

  /** Back to one room per connection: keep `keep` (or the first room), move the others out. */
  demote(keep = null) {
    this.mux = false;
    this.client.mux = false;
    const stay = keep || this.channels.values().next().value;
    for (const ch of Array.from(this.channels.values())) {
      if (ch === stay) continue;
      this.detach(ch, { leave: ch.joined || ch.joining });
      this.client.place(ch);
    }
  }

  attach(ch) {
    this.channels.set(ch.room, ch);
    ch.conn = this;
    if (!this.socket) {
      this.open();
      return;
    }
    if (!this.socket.connected) return;   // joined on connect
    if (this.client.requiresAuth(ch.room) && !this.hadCredential) {
      // The handshake carried no credential: reconnect with it (re-joins everything).
      this.restart();
      return;
    }
    this.joinWaiting();
  }

  detach(ch, { leave = true } = {}) {
    if (this.channels.get(ch.room) !== ch) return;
    this.channels.delete(ch.room);
    const wasJoined = ch.joined || ch.joining;
    ch.detached();
    ch.conn = null;
    if (this.socket?.connected && leave && wasJoined) this.socket.emit('leave::room', ch.room);
    if (!this.channels.size) this.close();
    // The probe join may have been this room's: let the next one go.
    else this.joinWaiting();
  }

  onDisconnect(reason) {
    this.channels.forEach((ch) => ch.detached());
    if (this.closed) return;
    // The Manager does not retry after a server-side disconnect.
    if (reason === 'io server disconnect') this.scheduleReconnect();
  }

  onConnectError() {
    if (this.closed || !this.socket) return;
    // `active` false: the handshake was refused, the Manager stopped retrying.
    if (!this.socket.active) this.scheduleReconnect();
  }

  onDenied(payload) {
    const ch = this.route(payload?.room);
    if (!ch) return;
    ch.joined = false;
    const code = payload?.code;
    if (code === 'token_refused' || code === 'no_token') {
      const refresh = this.client.refreshAuth;
      if (!refresh) return;
      const now = this.env.now();
      // At most once a minute: a user who lost the role must not loop.
      if (now - this.lastAuthRefresh < 60000) return;
      this.lastAuthRefresh = now;
      Promise.resolve()
        .then(() => refresh(ch.room, payload))
        .then((ok) => {
          if (ok && !this.closed && this.socket) this.restart();
        })
        .catch(() => {});
    } else if (code === 'too_many_rooms' && this.channels.size > 1) {
      // This connection is full: give the room a connection of its own.
      this.detach(ch, { leave: false });
      this.client.place(ch, { dedicated: true });
    } else if (payload?.retry) {
      // api_unavailable / server_full: notify retries api_unavailable by itself
      // (join::accepted); the rest is retried on the room's backoff.
      if (code !== 'api_unavailable') ch.scheduleRejoin();
    } else {
      this.client.logger?.warn?.(`[realtime] join refused for ${ch.room}: ${code || payload?.reason}`);
    }
  }

  onAccepted(payload) {
    const ch = this.route(payload?.room);
    if (ch) ch.onAccepted();
  }

  /**
   * Tab visible again / network back. Disconnected: retry now instead of waiting
   * for the backoff. Connected: the socket may be a zombie (frozen tab) - probe
   * it with a sync per room (one probe when no seq is known); no answer means
   * reconnect (once).
   */
  wake(reason) {
    if (this.closed || !this.socket) return;
    if (this.socket.connected) {
      const joined = Array.from(this.channels.values()).filter((ch) => ch.joined);
      if (!joined.length) return;
      const gen = ++this.wakeGen;
      const onTimeout = () => this.restartOnce(gen);
      const known = joined.filter((ch) => ch.lastSeq != null);
      if (known.length) known.forEach((ch) => ch.sync(reason, { onTimeout }));
      else this.probe(joined[0], onTimeout);
      return;
    }
    this.clearReconnectTimer();
    this.attempt = 0;
    if (this.socket.io && this.socket.io._readyState === 'opening') return;
    this.restart();
  }

  probe(ch, onTimeout) {
    const socket = this.socket;
    let done = false;
    const timer = this.env.setTimeout(() => {
      if (done) return;
      done = true;
      onTimeout();
    }, this.client.ackTimeout);
    socket.emit('sync::room', { room: ch.room, since: null }, () => {
      if (done) return;
      done = true;
      this.env.clearTimeout(timer);
    });
  }

  restartOnce(gen) {
    if (this.restartedGen === gen) return;
    this.restartedGen = gen;
    this.restart();
  }

  /** Drop the connection and open a new one (new credential, immediate retry). */
  restart() {
    if (this.closed || !this.socket) return;
    this.clearReconnectTimer();
    this.socket.disconnect();
    this.socket.connect();
  }

  scheduleReconnect() {
    if (this.reconnectTimer || this.closed) return;
    const delay = backoffDelay(this.attempt++, { random: this.client.random });
    this.reconnectTimer = this.env.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closed || !this.socket || this.socket.connected) return;
      this.socket.connect();
    }, delay);
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      this.env.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.clearReconnectTimer();
    if (this.client.connections.get(this.key) === this) this.client.connections.delete(this.key);
    const socket = this.socket;
    this.socket = null;
    // No leave::room: the disconnect frees every membership on the server.
    this.channels.forEach((ch) => {
      ch.detached();
      ch.conn = null;
    });
    this.channels.clear();
    if (!socket) return;
    if (typeof socket.offAny === 'function') socket.offAny();
    socket.disconnect();
  }
}

export class RealtimeClient {
  /**
   * @param {object} opts
   * @param {string} opts.url notify URL
   * @param {Function} opts.io socket.io-client `io`
   * @param {object} [opts.socketOptions]
   * @param {(room: string) => object | null} [opts.getAuth] credential sent on connect, per room
   * @param {(room: string, denied: object) => Promise<boolean> | boolean} [opts.refreshAuth]
   *        called on join::denied (token refused): true = new credential, reconnect
   * @param {(room: string) => string} [opts.authGroup] rooms of the same group share one
   *        connection (they must use the same credential). Default: one group.
   * @param {(room: string) => boolean} [opts.requiresAuth] rooms never joined without a credential
   * @param {object} [opts.env] timers/window/document (tests)
   * @param {object} [opts.logger]
   * @param {number} [opts.ackTimeout] ms to wait for a join/sync ack
   * @param {number} [opts.lingerMs] ms a released room stays joined (remounts)
   * @param {() => number} [opts.random]
   */
  constructor({ url, io, socketOptions = {}, getAuth, refreshAuth, authGroup, requiresAuth, env, logger, ackTimeout = 8000, lingerMs = 1500, random } = /** @type {any} */ ({})) {
    this.url = url;
    this.io = io;
    this.socketOptions = socketOptions;
    this.getAuth = getAuth;
    this.refreshAuth = refreshAuth;
    this.authGroup = authGroup || (() => 'default');
    this.requiresAuth = requiresAuth || (() => false);
    this.env = { ...defaultEnv(), ...(env || {}) };
    this.logger = logger === undefined ? console : logger;
    this.ackTimeout = ackTimeout;
    this.lingerMs = lingerMs;
    this.random = random || Math.random;
    /** Whether notify multiplexes (null: not known yet). */
    this.mux = null;
    /** @type {Map<string, Channel>} */
    this.channels = new Map();
    /** @type {Map<string, Connection>} */
    this.connections = new Map();
    /** Channels waiting for a credential. @type {Set<Channel>} */
    this.waitingAuth = new Set();
    /** Connections whose handshake lacked the credential some room needs. @type {Set<Connection>} */
    this.pendingConns = new Set();
    this.authTimer = null;
    this.authAttempt = 0;
    /** @type {Map<string, Set<{cb: Function, room: string | null}>>} */
    this.listeners = new Map();
    this.hiddenAt = null;
    this.envBound = false;
    this.onVisibility = () => {
      const doc = this.env.document;
      if (!doc) return;
      if (doc.visibilityState === 'hidden') {
        this.hiddenAt = this.env.now();
        return;
      }
      const hiddenFor = this.hiddenAt == null ? Infinity : this.env.now() - this.hiddenAt;
      this.hiddenAt = null;
      // A short switch away cannot have frozen the socket; still retry dead ones.
      this.connections.forEach((conn) => {
        if (!conn.socket?.connected || hiddenFor >= 10000) conn.wake('visible');
      });
    };
    this.onOnline = () => this.wake('online');
  }

  credentialFor(room) {
    try {
      return hasCredential(this.getAuth && this.getAuth(room));
    } catch {
      return false;
    }
  }

  /** Put a channel on a connection (shared when the server multiplexes). */
  place(ch, { dedicated = false } = {}) {
    if (ch.closed) return;
    if (this.requiresAuth(ch.room) && !this.credentialFor(ch.room)) {
      this.waitingAuth.add(ch);
      this.scheduleAuthCheck();
      return;
    }
    this.waitingAuth.delete(ch);
    let conn = null;
    if (!dedicated && this.mux !== false) {
      const key = `group:${ch.group}`;
      const current = this.connections.get(key);
      if (current && !current.closed && !(current.mux === false && current.channels.size)) {
        conn = current;
      } else if (!current || current.closed || !current.channels.size) {
        conn = new Connection(this, key, ch.group, this.mux);
      }
    }
    // Older notify (or a full shared connection): a connection for this room alone.
    if (!conn) conn = new Connection(this, `room:${ch.room}`, ch.group, false);
    this.connections.set(conn.key, conn);
    conn.attach(ch);
  }

  /** A connection opened without the credential its rooms need: retry when it shows up. */
  pendAuth(conn) {
    this.pendingConns.add(conn);
    this.scheduleAuthCheck();
  }

  scheduleAuthCheck() {
    if (this.authTimer) return;
    const delay = backoffDelay(this.authAttempt++, { random: this.random });
    this.authTimer = this.env.setTimeout(() => {
      this.authTimer = null;
      this.authChanged();
    }, delay);
  }

  /**
   * The credential changed (login, refresh, support session): join the rooms
   * that were waiting for it. Cheap; call it freely.
   */
  authChanged() {
    if (this.authTimer) {
      this.env.clearTimeout(this.authTimer);
      this.authTimer = null;
    }
    for (const ch of Array.from(this.waitingAuth)) {
      if (this.credentialFor(ch.room)) this.place(ch);
    }
    for (const conn of Array.from(this.pendingConns)) {
      if (conn.closed) {
        this.pendingConns.delete(conn);
        continue;
      }
      const room = conn.authRoom();
      if (room && this.credentialFor(room)) {
        this.pendingConns.delete(conn);
        conn.restart();
      }
    }
    if (this.waitingAuth.size || this.pendingConns.size) this.scheduleAuthCheck();
    else this.authAttempt = 0;
  }

  /**
   * Join a room (ref-counted). Returns the release function.
   * @param {string} room
   * @param {{onResync?: (info: {room: string, reason: string}) => void}} [opts]
   *        called when events may have been missed: refetch over REST
   * @returns {() => void}
   */
  join(room, { onResync } = {}) {
    if (!room || typeof room !== 'string') return () => {};
    if (!this.url || !this.io) return () => {};
    let ch = this.channels.get(room);
    if (!ch) {
      ch = new Channel(this, room);
      this.channels.set(room, ch);
      this.place(ch);
    }
    if (ch.closeTimer) {
      this.env.clearTimeout(ch.closeTimer);
      ch.closeTimer = null;
    }
    ch.refs += 1;
    if (onResync) ch.resyncHooks.add(onResync);
    this.bindEnv();
    const channel = ch;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (onResync) channel.resyncHooks.delete(onResync);
      channel.refs -= 1;
      if (channel.refs > 0 || channel.closed) return;
      const finish = () => {
        channel.closeTimer = null;
        if (channel.refs > 0 || channel.closed) return;
        this.drop(channel);
      };
      if (this.lingerMs > 0) channel.closeTimer = this.env.setTimeout(finish, this.lingerMs);
      else finish();
    };
  }

  /** Leave a room now: leave::room on its connection, closed with its last room. */
  drop(channel) {
    channel.closed = true;
    channel.clearRejoinTimer();
    this.waitingAuth.delete(channel);
    if (channel.conn) channel.conn.detach(channel);
    if (this.channels.get(channel.room) === channel) this.channels.delete(channel.room);
    if (!this.channels.size) this.idle();
  }

  idle() {
    if (this.authTimer) {
      this.env.clearTimeout(this.authTimer);
      this.authTimer = null;
    }
    this.authAttempt = 0;
    this.waitingAuth.clear();
    this.pendingConns.clear();
    this.unbindEnv();
  }

  /**
   * Listen to an event. Without `room`: every joined room. Returns the unsubscribe function.
   * The callback receives (data, {room, seq, replayed}).
   * @param {string} event
   * @param {Function} cb
   * @param {{room?: string | null}} [opts]
   */
  on(event, cb, { room = null } = {}) {
    if (!event || typeof cb !== 'function') return () => {};
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    const entry = { cb, room: room || null };
    set.add(entry);
    return () => {
      const current = this.listeners.get(event);
      if (!current) return;
      current.delete(entry);
      if (!current.size) this.listeners.delete(event);
    };
  }

  /** Remove every registration of `cb` for `event`. */
  off(event, cb) {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const entry of Array.from(set)) {
      if (entry.cb === cb) set.delete(entry);
    }
    if (!set.size) this.listeners.delete(event);
  }

  dispatch(room, event, data, meta) {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const entry of Array.from(set)) {
      if (entry.room && room && entry.room !== room) continue;
      try {
        entry.cb(data, meta);
      } catch (error) {
        this.logger?.error?.(`[realtime] error in ${event} handler:`, error);
      }
    }
  }

  /** Emit to the server, on the given room's connection (default: any connected one). */
  emit(event, data, room = null) {
    const conn = room
      ? this.channels.get(room)?.conn
      : Array.from(this.connections.values()).find((c) => c.socket?.connected);
    if (!conn?.socket?.connected) {
      this.logger?.warn?.(`[realtime] cannot emit ${event}: not connected`);
      return false;
    }
    conn.socket.emit(event, data);
    return true;
  }

  isConnected(room = null) {
    if (room) return !!this.channels.get(room)?.conn?.socket?.connected;
    return Array.from(this.connections.values()).some((c) => c.socket?.connected);
  }

  isJoined(room) {
    return !!this.channels.get(room)?.joined;
  }

  rooms() {
    return Array.from(this.channels.keys());
  }

  /** Open connections (one per auth group with a multiplexing notify). */
  connectionCount() {
    return this.connections.size;
  }

  lastSeq(room) {
    return this.channels.get(room)?.lastSeq ?? null;
  }

  wake(reason = 'wake') {
    this.connections.forEach((conn) => conn.wake(reason));
  }

  /** Leave every room now (logout). Listeners are kept. */
  closeAll() {
    this.channels.forEach((ch) => {
      ch.closed = true;
      ch.clearRejoinTimer();
      if (ch.closeTimer) {
        this.env.clearTimeout(ch.closeTimer);
        ch.closeTimer = null;
      }
    });
    Array.from(this.connections.values()).forEach((conn) => conn.close());
    this.connections.clear();
    this.channels.clear();
    this.idle();
  }

  bindEnv() {
    if (this.envBound) return;
    const { document: doc, window: win } = this.env;
    if (doc?.addEventListener) doc.addEventListener('visibilitychange', this.onVisibility);
    if (win?.addEventListener) win.addEventListener('online', this.onOnline);
    this.envBound = true;
  }

  unbindEnv() {
    if (!this.envBound) return;
    const { document: doc, window: win } = this.env;
    if (doc?.removeEventListener) doc.removeEventListener('visibilitychange', this.onVisibility);
    if (win?.removeEventListener) win.removeEventListener('online', this.onOnline);
    this.envBound = false;
  }
}
